import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import TickerResultCard from "./TickerResultCard";
import { toArray } from "../sports/tickerSportUtils";
import { FOOT_RESULT_DURATION } from "../../constants-ticker";

const getResultKey = (result, index) => result?.id || result?.opta_event_url || `${result?.left_name}-${result?.right_name}-${index}`;

/** Renderiza el arreglo `results`; FOOT muestra un encuentro verticalmente a la vez. */
export default function TickerResultsContent({
  results,
  league,
  sportCode,
  isActive = true,
}) {
  const resultList = toArray(results).filter(Boolean);
  const normalizedSport = String(sportCode || "").toUpperCase();
  // FOOT, BASE (MLB) y AMFO comparten el carrusel vertical: una tarjeta a la vez
  // que rota en Y. El resto de deportes mantiene el listado horizontal.
  const isVerticalCarousel = ["FOOT", "BASE", "AMFO"].includes(normalizedSport);
  const [activeIndex, setActiveIndex] = useState(0);
  const [availableWidth, setAvailableWidth] = useState(0);
  const carouselRef = useRef(null);
  const resultsKey = resultList.map(getResultKey).join("|");

  // Duración de la animación de entrada del slide (debe coincidir con el
  // `transition.duration` del motion.div de abajo).
  const SLIDE_ENTER_MS = 400;

  useLayoutEffect(() => {
    if (!isVerticalCarousel || !carouselRef.current) return undefined;

    const wrapper = carouselRef.current.closest(".ticker-content-wrapper");
    if (!wrapper) return undefined;

    const updateWidth = () => setAvailableWidth(wrapper.getBoundingClientRect().width);
    updateWidth();

    if (typeof ResizeObserver === "undefined") return undefined;

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(wrapper);
    return () => resizeObserver.disconnect();
  }, [isVerticalCarousel]);

  // Reinicia el carrusel al índice 0 cuando el bloque se activa/desactiva o
  // cambia el conjunto de resultados.
  useEffect(() => {
    setActiveIndex(0);
  }, [isActive, isVerticalCarousel, resultsKey]);

  // Programa el avance del resultado visible. El tiempo de permanencia empieza a
  // contar una vez que la tarjeta llegó a su posición final: por eso se suma la
  // duración de la animación de entrada (SLIDE_ENTER_MS) al primer retardo.
  // El efecto se re-ejecuta con cada cambio de activeIndex, así que cada tarjeta
  // dispone de su propio ciclo completo antes de dar paso a la siguiente.
  useEffect(() => {
    if (!isVerticalCarousel || !isActive || resultList.length < 2) return undefined;

    const timer = window.setTimeout(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % resultList.length);
    }, SLIDE_ENTER_MS + FOOT_RESULT_DURATION);

    return () => window.clearTimeout(timer);
  }, [activeIndex, isActive, isVerticalCarousel, resultList.length, resultsKey]);

  if (!isVerticalCarousel) {
    return resultList.map((result, index) => (
      <TickerResultCard
        key={`result-${getResultKey(result, index)}`}
        result={result}
        league={league}
        sportCode={sportCode}
      />
    ));
  }

  const activeResult = resultList[activeIndex];
  if (!activeResult) return null;

  return (
    <div
      ref={carouselRef}
      className="ticker-foot-results-carousel"
      style={availableWidth ? { width: `${availableWidth}px`, flexBasis: `${availableWidth}px` } : undefined}
    >
      <AnimatePresence initial={false}>
        <motion.div
          key={getResultKey(activeResult, activeIndex)}
          className="ticker-foot-results-carousel__slide"
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "-100%", opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
        >
          <TickerResultCard result={activeResult} league={league} sportCode={sportCode} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
